import React, { useEffect, useMemo, useState } from 'react';
import { Category, CategoryGroup, Subcategory } from '../types/api';
import {
  CategorySection,
  allCategoryIds,
  allowedSubcategories,
  categorySections,
  pruneSubcategories,
  relevantGroups,
} from '../utils/taxonomy';

export interface TaxonomySelection {
  /** Основная категория — по ней заведение представлено в каталоге. */
  primary_category_id: number | null;
  /** Дополнительные категории, без основной. */
  categories: number[];
  subcategories: number[];
}

interface TaxonomyEditorProps {
  groups: CategoryGroup[];
  /** Плоский справочник: категории, не вошедшие ни в один раздел, идут в «Другое». */
  categories: Category[];
  value: TaxonomySelection;
  onChange: (next: TaxonomySelection) => void;
  disabled?: boolean;
}

type PickerMode = 'primary' | 'additional' | 'subcategories';

const MODE_TITLES: Record<PickerMode, string> = {
  primary: 'Основная категория',
  additional: 'Дополнительные категории',
  subcategories: 'Специализация',
};

const MODE_HINTS: Record<PickerMode, string> = {
  primary: 'Чем занимается заведение. Одна на выбор — по ней вас найдут в каталоге.',
  additional: 'Если заведение смешанное: ресторан и бар одновременно.',
  subcategories: 'Кухни и виды услуг. Доступны только те, что подходят вашим категориям.',
};

/** Раздел для категорий вне разделов — тот же, что в анкете регистрации. */
const OTHER_SECTION_TITLE = 'Другое';

const matches = (title: string, query: string) => title.toLowerCase().includes(query);

/** Категории под поиск; разделы, где ничего не нашлось, прячем. */
function filterSections(sections: CategorySection[], query: string): CategorySection[] {
  if (!query) return sections;

  return sections
    .map((section) => ({
      ...section,
      categories: section.categories.filter((category) => matches(category.title, query)),
    }))
    .filter((section) => section.categories.length > 0);
}

/** Доступные подкатегории по группам, без дублей. */
function subcategoriesByGroup(groups: CategoryGroup[], categoryIds: number[], query: string) {
  const available = new Set(allowedSubcategories(groups, categoryIds).map((s) => s.id));
  const seen = new Set<number>();

  return relevantGroups(groups, categoryIds)
    .map((group) => {
      const subcategories = group.subcategories.filter((subcategory) => {
        if (!available.has(subcategory.id) || seen.has(subcategory.id)) return false;
        if (query && !matches(subcategory.title, query)) return false;
        seen.add(subcategory.id);
        return true;
      });

      return { group, subcategories };
    })
    .filter((entry) => entry.subcategories.length > 0);
}

export const TaxonomyEditor: React.FC<TaxonomyEditorProps> = ({ groups, categories, value, onChange, disabled }) => {
  const [mode, setMode] = useState<PickerMode | null>(null);
  const [query, setQuery] = useState('');

  const selectedCategoryIds = useMemo(
    () => allCategoryIds(value.primary_category_id, value.categories),
    [value.primary_category_id, value.categories],
  );

  // Категории по разделам, каждая ровно один раз: «Бары» есть и в «Поесть»,
  // и в «Развлечениях». Те, что вне разделов, — в конце, в «Другом».
  const sections = useMemo(
    () => categorySections(groups, categories, OTHER_SECTION_TITLE),
    [groups, categories],
  );

  // Из разделов, а не только из дерева: иначе у категории из «Другого»
  // в поле вместо названия был бы прочерк.
  const categoryById = useMemo(() => {
    const map = new Map<number, Category>();
    sections.forEach((section) => section.categories.forEach((c) => map.set(c.id, c)));
    return map;
  }, [sections]);

  const subcategoryById = useMemo(() => {
    const map = new Map<number, Subcategory>();
    groups.forEach((group) => group.subcategories.forEach((s) => map.set(s.id, s)));
    return map;
  }, [groups]);

  const availableSubcategories = useMemo(
    () => allowedSubcategories(groups, selectedCategoryIds),
    [groups, selectedCategoryIds],
  );

  useEffect(() => {
    if (!mode) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMode(null);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mode]);

  const openPicker = (next: PickerMode) => {
    setQuery('');
    setMode(next);
  };

  /** Смена категорий может осиротить подкатегории — обрезаем их так же, как сервер. */
  const applyCategories = (primaryId: number | null, additional: number[]) => {
    const nextCategoryIds = allCategoryIds(primaryId, additional);

    onChange({
      primary_category_id: primaryId,
      categories: additional.filter((id) => id !== primaryId),
      subcategories: pruneSubcategories(groups, nextCategoryIds, value.subcategories),
    });
  };

  const selectPrimary = (categoryId: number) => {
    applyCategories(categoryId, value.categories);
    setMode(null);
  };

  const toggleAdditional = (categoryId: number) => {
    const next = value.categories.includes(categoryId)
      ? value.categories.filter((id) => id !== categoryId)
      : [...value.categories, categoryId];

    applyCategories(value.primary_category_id, next);
  };

  const toggleSubcategory = (subcategoryId: number) => {
    const next = value.subcategories.includes(subcategoryId)
      ? value.subcategories.filter((id) => id !== subcategoryId)
      : [...value.subcategories, subcategoryId];

    onChange({ ...value, subcategories: next });
  };

  const primaryTitle = value.primary_category_id
    ? categoryById.get(value.primary_category_id)?.title ?? '—'
    : null;

  const additionalTitles = value.categories
    .map((id) => categoryById.get(id)?.title)
    .filter(Boolean)
    .join(', ');

  const subcategoryTitles = value.subcategories
    .map((id) => subcategoryById.get(id)?.title)
    .filter(Boolean)
    .join(', ');

  const normalizedQuery = query.trim().toLowerCase();

  return (
    <div className="qp-fields">
      <FieldButton
        label="Основная категория"
        value={primaryTitle}
        placeholder="Выберите категорию"
        onClick={() => openPicker('primary')}
        disabled={disabled}
      />

      <FieldButton
        label="Дополнительные категории"
        value={value.categories.length > 0 ? additionalTitles : null}
        count={value.categories.length}
        placeholder="Не выбраны"
        onClick={() => openPicker('additional')}
        disabled={disabled}
      />

      <FieldButton
        label="Специализация"
        value={value.subcategories.length > 0 ? subcategoryTitles : null}
        count={value.subcategories.length}
        placeholder={
          selectedCategoryIds.length === 0
            ? 'Сначала выберите категорию'
            : availableSubcategories.length === 0
              ? 'Для этих категорий не заведена'
              : 'Не выбрана'
        }
        onClick={() => openPicker('subcategories')}
        disabled={disabled || availableSubcategories.length === 0}
        wide
      />

      {mode && (
        <div
          className="qp-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="taxonomy-dialog-title"
          onClick={() => setMode(null)}
        >
          <div className="qp-dialog-card" onClick={(e) => e.stopPropagation()}>
            <header className="qp-dialog-head">
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 id="taxonomy-dialog-title" className="qp-dialog-title">
                  {MODE_TITLES[mode]}
                </h2>
                <p className="qp-dialog-hint">{MODE_HINTS[mode]}</p>
              </div>
              <button
                type="button"
                className="qp-btn qp-btn--icon"
                onClick={() => setMode(null)}
                aria-label="Закрыть"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </header>

            <div style={{ padding: '0 18px 12px' }}>
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск…"
                className="qp-control"
              />
            </div>

            <div className="qp-dialog-body">
              {mode === 'subcategories' ? renderSubcategories() : renderCategories(mode)}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderCategories(currentMode: 'primary' | 'additional') {
    const entries = filterSections(sections, normalizedQuery);

    if (entries.length === 0) return <EmptyState text="Ничего не найдено" />;

    return entries.map((section) => (
      <section key={section.key} style={{ marginBottom: 8 }}>
        {section.title !== null && <GroupHeading title={section.title} />}
        {section.categories.map((category) => {
          const isPrimary = category.id === value.primary_category_id;
          const checked = currentMode === 'primary' ? isPrimary : isPrimary || value.categories.includes(category.id);

          return (
            <OptionRow
              key={category.id}
              title={category.title}
              badge={currentMode === 'additional' && isPrimary ? 'основная' : undefined}
              checked={checked}
              disabled={currentMode === 'additional' && isPrimary}
              onClick={() =>
                currentMode === 'primary' ? selectPrimary(category.id) : toggleAdditional(category.id)
              }
            />
          );
        })}
      </section>
    ));
  }

  function renderSubcategories() {
    if (selectedCategoryIds.length === 0) {
      return <EmptyState text="Сначала выберите категорию заведения" />;
    }

    const entries = subcategoriesByGroup(groups, selectedCategoryIds, normalizedQuery);

    if (entries.length === 0) return <EmptyState text="Ничего не найдено" />;

    return entries.map(({ group, subcategories }) => (
      <section key={group.id} style={{ marginBottom: 8 }}>
        <GroupHeading title={group.title} />
        {subcategories.map((subcategory) => (
          <OptionRow
            key={subcategory.id}
            title={subcategory.title}
            checked={value.subcategories.includes(subcategory.id)}
            onClick={() => toggleSubcategory(subcategory.id)}
          />
        ))}
      </section>
    ));
  }
};

interface FieldButtonProps {
  label: string;
  value: string | null;
  count?: number;
  placeholder: string;
  onClick: () => void;
  disabled?: boolean;
  /** Занять обе колонки сетки полей. */
  wide?: boolean;
}

/** Поле, которое заполняется через диалог: выглядит как ввод, ведёт себя как кнопка. */
const FieldButton: React.FC<FieldButtonProps> = ({
  label,
  value,
  count,
  placeholder,
  onClick,
  disabled,
  wide,
}) => (
  <div className={`qp-field${wide ? ' qp-field--wide' : ''}`}>
    <span className="qp-field-label">{label}</span>
    <button type="button" onClick={onClick} className="qp-control qp-fieldbtn" disabled={disabled}>
      <span className={`qp-fieldbtn-value${value ? '' : ' qp-fieldbtn-value--empty'}`}>
        {value ?? placeholder}
      </span>
      {count !== undefined && count > 1 && <span className="qp-fieldbtn-count">{count}</span>}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M9 6l6 6-6 6" strokeLinecap="round" />
      </svg>
    </button>
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <p style={{ margin: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--qp2-faint)' }}>{text}</p>
);

const GroupHeading: React.FC<{ title: string }> = ({ title }) => (
  <p className="qp-optgroup">{title}</p>
);

interface OptionRowProps {
  title: string;
  checked: boolean;
  badge?: string;
  disabled?: boolean;
  onClick: () => void;
}

const OptionRow: React.FC<OptionRowProps> = ({ title, checked, badge, disabled, onClick }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-pressed={checked} className="qp-option">
    <span style={{ flex: 1, minWidth: 0 }}>{title}</span>
    {badge && <span className="qp-option-badge">{badge}</span>}
    {checked && (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
        <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);
